import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import appConfig from "appConfig";
import { toast } from "react-toastify";
import useAuthEntra from 'hooks/useAuthPharo';
import { useUserInfo } from 'hooks/useUserInfo';
import { trackCustomEvent } from 'utils/telemetry';
import { getAppSource } from 'utils/shellDetection';
import { useState, useEffect } from 'react';
import SessionExpiredDialog from 'components/auth/SessionExpiredDialog';

// Global flag to prevent multiple simultaneous logout attempts
let isLogoutInProgress = false;

// Global refresh lock to prevent multiple simultaneous token refreshes
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
let refreshedToken: string | null = null; // Store the refreshed token for immediate use

// Global session dialog state (shared across all useApi instances)
let globalShowSessionDialog = false;
let globalSessionDialogError: string | undefined = undefined;
let sessionDialogUpdateListeners: Set<() => void> = new Set();

// Types for enhanced error tracking
interface AuthErrorContext {
  originalUrl: string;
  originalMethod: string;
  timestamp: string;
  userAgent: string;
  sessionId?: string;
  tokenInfo?: {
    hasToken: boolean;
    tokenLength: number;
    tokenPreview: string;
  };
}

// Example interface for your typical API response
export interface ApiResponse<T> {
  data: T;
  warnings?: string[];
  errors?: string[];
  status?: string;
  errorType?: string;
}

/**
 * useApi:
 *   - Central place for all API calls
 *   - Handles 400 & 500 errors, shows toast
 *   - On 401, tries token refresh via silent authentication
 *   - On failing to refresh, shows user-friendly dialog and attempts interactive login
 *   - Enhanced logging for better troubleshooting
 */
export default function useApi() {
  const { getCurrentToken, refreshToken, logout, login, user } = useAuthEntra();
  const {  
    userName,
    userInitials,
    signedInUserName,
  } = useUserInfo();
  
  // Local state that syncs with global state
  const [showSessionDialog, setShowSessionDialog] = useState(globalShowSessionDialog);
  const [sessionDialogError, setSessionDialogError] = useState<string | undefined>(globalSessionDialogError);

  // Subscribe to global state changes
  useEffect(() => {
    const listener = () => {
      setShowSessionDialog(globalShowSessionDialog);
      setSessionDialogError(globalSessionDialogError);
    };
    
    sessionDialogUpdateListeners.add(listener);
    
    return () => {
      sessionDialogUpdateListeners.delete(listener);
    };
  }, []);

  // Helper to update global state and notify all listeners
  const updateGlobalSessionDialog = (show: boolean, error?: string) => {
    globalShowSessionDialog = show;
    globalSessionDialogError = error;
    
    console.warn('🚨 GLOBAL SESSION DIALOG UPDATED:', { 
      show, 
      error,
      listenerCount: sessionDialogUpdateListeners.size 
    });
    
    // Notify all listeners
    sessionDialogUpdateListeners.forEach(listener => listener());
  };

  // Helper function to create auth error context for logging
  const createAuthErrorContext = (url: string, method: string, token: string | null): AuthErrorContext => {
    return {
      originalUrl: url,
      originalMethod: method,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: sessionStorage.getItem('sessionId') || undefined,
      tokenInfo: token ? {
        hasToken: true,
        tokenLength: token.length,
        tokenPreview: `${token.substring(0, 20)}...`
      } : {
        hasToken: false,
        tokenLength: 0,
        tokenPreview: 'null'
      }
    };
  };

  // The main function for making calls
  const apiRequest = async <T>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: any,
    customHeaders: Record<string, string> = {}
  ): Promise<ApiResponse<T> | null> => {
    let token = getCurrentToken();
    const appSource = getAppSource();
    const clientEnvironment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';
    
    

    const config: AxiosRequestConfig = {
      method,
      url,
      data,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Impersonated-User': signedInUserName !== userName ? userInitials : '',
        'X-App-Source': appSource,
        'X-App-Version': appConfig.version || 'unknown',
        'X-Client-Environment': clientEnvironment,
        ...customHeaders
      }
    };

    try {
      const response: AxiosResponse<ApiResponse<T>> = await axios(config);
      return response.data;
    } catch (error) {
      // Check if it's an AxiosError
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        


        if (status === 401) {
          const authContext = createAuthErrorContext(url, method, token);
          
          // Check if this request already attempted a retry
          if ((config as any)._retryAttempted) {
            console.error('>>> 401 after retry - not attempting again', {
              url: config.url,
              timestamp: new Date().toISOString()
            });
            
            // Don't retry again, let it fall through to normal error handling
            throw error;
          }
          
          // Enhanced logging for initial 401
          console.warn('🚨 401 ERROR DETECTED - Starting enhanced auth recovery...', authContext);
          console.warn('🚨 401 ERROR - Current session dialog state:', {
            showSessionDialog,
            sessionDialogError,
            isLogoutInProgress
          });
          
          // Enhanced Application Insights logging for 401 errors
          trackCustomEvent('AuthenticationError_401_Received', {
            ...authContext,
            user: signedInUserName,
            currentUrl: window.location.href,
            errorDetails: error.response?.data || 'No additional error details',
            stackTrace: error.stack || 'No stack trace available'
          });

          // Prevent multiple simultaneous logout attempts
          if (isLogoutInProgress) {
            console.warn('>>> Logout already in progress, ignoring additional 401');
            return null;
          }

          try {
            // Use refresh lock to prevent multiple simultaneous refreshes
            if (isRefreshing && refreshPromise) {
              console.log('>>> Token refresh already in progress, waiting...');
              await refreshPromise; // Wait for the ongoing refresh to complete
            } else {
              console.log('>>> Attempting silent token refresh...');
              isRefreshing = true;
              refreshedToken = null; // Reset before refresh
              
              // Create a promise for this refresh that others can wait on
              refreshPromise = refreshToken()
                .then((newToken) => {
                  console.log('>>> Token refresh completed successfully');
                  refreshedToken = newToken; // Store the new token globally
                })
                .catch((err) => {
                  console.error('>>> Token refresh failed:', err);
                  refreshedToken = null;
                  throw err;
                })
                .finally(() => {
                  isRefreshing = false;
                  refreshPromise = null;
                });
              
              await refreshPromise;
            }
            
            // Use the refreshed token directly (don't wait for React state to update)
            token = refreshedToken;
            
            // Validate we got a token
            if (!token) {
              throw new Error('Token refresh succeeded but returned null token');
            }
            
            console.log('>>> Token refresh completed, waiting before retry', {
              timestamp: new Date().toISOString(),
              newToken: token ? `${token.substring(0, 20)}...` : 'null',
              newTokenLength: token?.length || 0,
              originalRequest: `${method.toUpperCase()} ${url}`,
              tokenSource: 'refreshedToken (direct from refresh, bypassing React state)'
            });
            
            // CRITICAL: Wait longer to let backend EasyAuth session cache update
            // Tests show 750ms wasn't enough, increasing to 1500ms for maximum safety
            console.log('>>> Waiting 1500ms for backend session cache to sync...');
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Log successful token refresh
            trackCustomEvent('AuthenticationError_TokenRefreshSucceeded', {
              ...authContext,
              user: signedInUserName,
              newTokenLength: token?.length || 0,
              refreshDurationMs: Date.now() - new Date(authContext.timestamp).getTime()
            });
            
            // Mark this request as a retry to prevent retry loops
            (config as any)._retryAttempted = true;
            
            // Retry the request with new token and cache-busting parameter
            config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
            
            // Add cache-busting parameter to force backend to validate session fresh
            const separator = url.includes('?') ? '&' : '?';
            config.url = `${url}${separator}_sessionRefresh=${Date.now()}`;
            
            console.log('>>> Retrying request with fresh token', {
              url: config.url,
              timestamp: new Date().toISOString()
            });
            
            const retryResponse: AxiosResponse<ApiResponse<T>> = await axios(config);
            
            console.log('>>> Retry request successful', {
              method,
              url,
              status: retryResponse.status,
              timestamp: new Date().toISOString()
            });
            
            // Log successful retry
            trackCustomEvent('AuthenticationError_RetrySucceeded', {
              ...authContext,
              user: signedInUserName,
              retryStatus: retryResponse.status,
              totalRecoveryDurationMs: Date.now() - new Date(authContext.timestamp).getTime()
            });
            
            return retryResponse.data;
          } catch (refreshError) {
            // Check if this is a retry failure (401 after token refresh succeeded)
            const isRetryFailure = axios.isAxiosError(refreshError) && 
                                  refreshError.response?.status === 401 &&
                                  (refreshError.config as any)?._retryAttempted;
            
            if (isRetryFailure) {
              console.error('>>> Retry failed with 401 after successful token refresh', {
                timestamp: new Date().toISOString(),
                message: 'Backend did not recognize new token even after 1500ms delay',
                suggestion: 'User can try navigating again - fresh requests should work',
                authContext
              });
              
              // Log retry failure specifically
              trackCustomEvent('AuthenticationError_RetryFailed_AfterSuccessfulRefresh', {
                ...authContext,
                user: signedInUserName,
                delay: '1500ms',
                suggestion: 'May need longer delay or different approach'
              });
            }
            
            isLogoutInProgress = true; // Set flag to prevent multiple logout attempts
            
            const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : String(refreshError);
            const refreshErrorType = refreshError instanceof Error ? refreshError.constructor.name : typeof refreshError;
            
            const errorContext = isRetryFailure 
              ? '>>> Retry with refreshed token failed (backend cache issue)'
              : '>>> Token refresh failed';
            
            console.error(errorContext + ', initiating graceful logout...', {
              timestamp: new Date().toISOString(),
              refreshError: refreshErrorMessage,
              refreshErrorType,
              isRetryFailure,
              authContext
            });

            // Enhanced Application Insights logging for token refresh failure
            trackCustomEvent('AuthenticationError_TokenRefreshFailed', {
              ...authContext,
              user: signedInUserName,
              refreshError: refreshErrorMessage,
              refreshErrorType,
              errorCode: (refreshError as any)?.errorCode || 'unknown',
              errorSubCode: (refreshError as any)?.subError || 'unknown',
              correlationId: (refreshError as any)?.correlationId || 'unknown',
              stackTrace: (refreshError as any)?.stack || 'No stack trace',
              currentUrl: window.location.href,
              userAgent: navigator.userAgent,
              isInteractionRequired: refreshErrorType === 'InteractionRequiredAuthError'
            });

            // Determine if we can try interactive login or must force logout
            const isInteractionRequired = refreshErrorType === 'InteractionRequiredAuthError';
            const canTryInteractiveLogin = isInteractionRequired && typeof login === 'function';
            
            // Set dialog error details for user
            const userErrorMessage = isInteractionRequired 
              ? 'Your session requires interactive authentication that cannot be performed automatically.'
              : `Authentication failed: ${refreshErrorMessage}`;
            
            console.warn('🚨 SETTING SESSION DIALOG STATE:', {
              userErrorMessage,
              canTryInteractiveLogin,
              isInteractionRequired
            });
            
            updateGlobalSessionDialog(true, userErrorMessage);
            
            console.warn('🚨 SESSION DIALOG STATE SET - Should trigger dialog now!');

            // Log the session expiry event with recovery attempt info
            trackCustomEvent('UserSessionExpired', {
              ...authContext,
              user: signedInUserName,
              reason: 'Token refresh failed after 401',
              canTryInteractiveLogin,
              isInteractionRequired,
              refreshError: refreshErrorMessage,
              willAttemptInteractiveLogin: canTryInteractiveLogin
            });
            
            // Track that the session expired dialog was shown to the user
            trackCustomEvent('SessionExpiredDialog_Shown', {
              user: signedInUserName,
              errorMessage: userErrorMessage,
              canTryInteractiveLogin,
              isInteractionRequired,
              refreshError: refreshErrorMessage,
              timestamp: new Date().toISOString(),
              currentUrl: window.location.href
            });

            return null;
          }
        }

        if (status === 400) {
          toast.error('Bad Request: Check your inputs or permissions.');
          return null;
        }
        if (status === 403) {
          toast.error('Access Denied: You do not have permission.');
          return null;
        }
        if (status === 500) {
          toast.error('Server Error: Please try again later.');
          return null;
        }
      }

      // Some other error
      console.error('API error:', error);
      toast.error('Unexpected error. Please try again.');
      return null;
    }
  };

  // Handler for session expired dialog
  const handleSessionExpiredConfirm = async () => {
    updateGlobalSessionDialog(false, undefined);
    isLogoutInProgress = false; // Reset flag
    
    // Determine if we can try interactive login
    const canTryInteractiveLogin = typeof login === 'function';
    
    if (canTryInteractiveLogin) {
      try {
        console.log('>>> Attempting interactive login...');
        await login(); // Try interactive login
        
        trackCustomEvent('AuthenticationError_InteractiveLoginAttempted', {
          user: signedInUserName,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        });
      } catch (loginError) {
        console.error('>>> Interactive login failed, forcing logout...', loginError);
        
        trackCustomEvent('AuthenticationError_InteractiveLoginFailed', {
          user: signedInUserName,
          timestamp: new Date().toISOString(),
          loginError: loginError instanceof Error ? loginError.message : String(loginError)
        });
        
        toast.error('Unable to sign in. Please try again.');
        logout(); // Force user to sign out
      }
    } else {
      // Fall back to forced logout
      console.log('>>> No interactive login available, forcing logout...');
      toast.error('Session expired. Please log in again.');
      logout();
    }
  };

  // Helpers for convenience
  const get = <T>(url: string, headers?: Record<string, string>) => apiRequest<T>('get', url, undefined, headers);
  const post = <T>(url: string, data?: any, headers?: Record<string, string>) => apiRequest<T>('post', url, data, headers);
  const put = <T>(url: string, data?: any, headers?: Record<string, string>) => apiRequest<T>('put', url, data, headers);
  const del = <T>(url: string, headers?: Record<string, string>) => apiRequest<T>('delete', url, undefined, headers);

  return { 
    get, 
    post, 
    put, 
    del,
    // Session dialog state and handlers for external use
    sessionDialog: {
      isOpen: showSessionDialog,
      error: sessionDialogError,
      onConfirm: handleSessionExpiredConfirm,
      isInteractiveLoginAvailable: typeof login === 'function'
    }
  };
}
